import { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ImportPeopleResponse, ImportPeopleRowInput } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/** A spreadsheet export rarely spells a header exactly `firstName` — this
 *  maps the common variants a church office would actually produce onto
 *  the canonical row keys the server expects. */
const HEADER_ALIASES: Record<string, keyof ImportPeopleRowInput> = {
  firstname: 'firstName',
  'first name': 'firstName',
  first: 'firstName',
  lastname: 'lastName',
  'last name': 'lastName',
  last: 'lastName',
  email: 'email',
  'e-mail': 'email',
  'email address': 'email',
  phone: 'phone',
  'phone number': 'phone',
  mobile: 'phone',
  'mobile number': 'phone',
  'mobile phone': 'phone',
};

function normalizeHeader(header: string): string {
  return HEADER_ALIASES[header.trim().toLowerCase()] ?? header.trim();
}

/**
 * Bulk-imports a roster CSV.
 *
 * Parsed entirely in the browser — this platform has no multipart-upload
 * handling anywhere, and client-side parsing surfaces a malformed file
 * before it ever reaches the network. The server receives structured rows,
 * validates and dedups each one independently (see
 * `PeopleService.importPeople`), and returns a per-row outcome the
 * results table below renders.
 */
export function ImportPeopleDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ImportPeopleRowInput[] | null>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: (rowsToImport: readonly ImportPeopleRowInput[]) =>
      peopleApi.importPeople({ rows: [...rowsToImport] }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['people'], exact: false }),
  });

  function handleFile(file: File) {
    setParseError(null);
    setRows(null);
    setFileName(file.name);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: normalizeHeader,
      complete: (results) => {
        const parseFailure = results.errors[0];
        if (parseFailure) {
          setParseError(parseFailure.message);
          return;
        }

        const parsedRows: ImportPeopleRowInput[] = [];
        for (const record of results.data) {
          const firstName = record.firstName?.trim() ?? '';
          const lastName = record.lastName?.trim() ?? '';
          // A trailing blank line in the file is not a row to report an
          // error on — it just isn't one.
          if (!firstName && !lastName) continue;
          parsedRows.push({
            firstName,
            lastName,
            email: record.email?.trim() || null,
            phone: record.phone?.trim() || null,
          });
        }

        if (parsedRows.length === 0) {
          setParseError(t('people.import.noRows'));
          return;
        }
        setRows(parsedRows);
      },
      error: (error: Error) => setParseError(error.message),
    });
  }

  const result = mutation.data;

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="import-people-title"
    >
      <div className="dialog-body">
        <h2 id="import-people-title">{t('people.import.title')}</h2>

        {!result && (
          <>
            <p className="person-card-meta">{t('people.import.hint')}</p>

            <Field
              label={t('people.import.fileLabel')}
              name="import-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) handleFile(file);
              }}
            />

            {fileName && rows && (
              <p className="settings-status-title">
                {t('people.import.rowsFound', { values: { count: rows.length } })}
              </p>
            )}
            {parseError && <Alert tone="danger">{parseError}</Alert>}
            {mutation.isError && <Alert tone="danger">{describeError(mutation.error, t)}</Alert>}
          </>
        )}

        {result && <ImportResultsSummary result={result} t={t} />}

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {result ? t('common.actions.close') : t('common.actions.cancel')}
          </Button>
          {!result && (
            <Button
              type="button"
              variant="primary"
              disabled={!rows || rows.length === 0}
              loading={mutation.isPending}
              onClick={() => rows && mutation.mutate(rows)}
            >
              {t('people.import.submit')}
            </Button>
          )}
        </footer>
      </div>
    </dialog>
  );
}

function ImportResultsSummary({
  result,
  t,
}: {
  result: ImportPeopleResponse;
  t: ReturnType<typeof useI18n>['t'];
}) {
  const problemRows = result.results.filter((row) => row.status !== 'created');

  return (
    <div>
      <p className="settings-status-title">
        {t('people.import.summary', {
          values: {
            created: result.created,
            updated: result.updated,
            skipped: result.skipped,
            errors: result.errors,
          },
        })}
      </p>

      {problemRows.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>{t('people.import.row')}</th>
                <th>{t('common.labels.status')}</th>
                <th>{t('people.import.message')}</th>
              </tr>
            </thead>
            <tbody>
              {problemRows.map((row) => (
                <tr key={row.row}>
                  <td>{row.row}</td>
                  <td>{t(`people.import.status.${row.status}`)}</td>
                  <td>{row.message ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
